<template>
  <div class="tw:flex tw:flex-col tw:w-full">
    <div class="tw:flex-1 tw:sm:px-[4.75rem] tw:xl:px-[11rem] tw:pt-16 tw:pb-4 tw:sm:pt-0 sm:pb-0">
      <div class="tw:flex tw:flex-col tw:justify-center tw:gap-4 tw:sm:gap-4 tw:w-full">
        <div class="tw:text-center tw:sm:text-left">
          <EdroneHeader
            :title="trans._register.platforms.title"
            :desc="trans._register.platforms.subtitle"
            :show-progress="true"
            :current-step="1"
            :total-steps="4"
          />
        </div>
        <div class="tw:space-y-2">
          <div class="tw:flex tw:justify-center tw:mb-4">
            <EdroneBadge
              type="info"
              :label="trans._register.platforms.popularPlatforms"
              icon="fa-light fa-star"
              size="small"
            />
          </div>
          <div
            class="tw:grid tw:grid-cols-2 tw:gap-2 tw:sm:gap-2"
            role="radiogroup"
            :aria-label="trans._register.platforms.title"
          >
            <button
              v-for="(platform, key) in platforms"
              :key="key"
              class="tw:h-20 tw:py-6 tw:px-4 tw:border tw:rounded-lg tw:transition-all tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:duration-200 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:ring-offset-2 tw:transform tw:hover:scale-102 tw:active:scale-95"
              :class="{
                'tw:bg-blue-000 tw:border-blue-500 tw:hover:border-blue-600 tw:shadow-md': selectedPlatform === key,
                'tw:bg-white tw:border-default-200 tw:hover:border-blue-500 tw:hover:shadow-xs':
                  selectedPlatform !== key,
              }"
              role="radio"
              :aria-checked="selectedPlatform === key"
              :aria-label="`${trans._register.platforms.select} ${platform.name}`"
              @click="handlePlatformSelect(key)"
              @keydown.enter="handlePlatformSelect(key)"
              @keydown.space.prevent="handlePlatformSelect(key)"
            >
              <img
                :src="platform.logo"
                :alt="platform.name"
                class="tw:h-24 tw:w-24 tw:object-contain"
                loading="lazy"
                :onerror="`this.style.display='none'`"
              />
            </button>
          </div>
          <button
            class="tw:w-full tw:border tw:rounded-lg tw:transition-all tw:h-20 tw:duration-200 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:ring-offset-2 tw:transform tw:hover:scale-102 tw:active:scale-95 tw:relative tw:overflow-hidden"
            :class="{
              'tw:bg-blue-000 tw:border-blue-500 tw:hover:border-blue-600 tw:shadow-md': isSelectedPlatformFromModal,
              'tw:bg-white tw:border-default-200 tw:hover:border-blue-500 tw:hover:shadow-lg tw:border-dashed':
                !isSelectedPlatformFromModal,
            }"
            :aria-label="`${trans._register.platforms.other.title} - ${trans._register.platforms.other.subtitle}`"
            :aria-expanded="showModal"
            :aria-describedby="isSelectedPlatformFromModal ? 'selected-platform' : 'other-platform-description'"
            @click="handleModalOpen"
            @keydown.enter="handleModalOpen"
            @keydown.space.prevent="handleModalOpen"
          >
            <div
              v-if="isSelectedPlatformFromModal"
              id="selected-platform"
              class="tw:flex tw:items-center tw:justify-center tw:gap-3 tw:h-full"
            >
              <img
                :src="selectedPlatformData?.logo"
                :alt="selectedPlatformData?.name"
                class="tw:w-24 tw:object-contain"
                loading="lazy"
                :onerror="`this.style.display='none'`"
              />
            </div>
            <div
              v-else
              id="other-platform-description"
              class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:gap-2"
              role="img"
              :aria-label="`${trans._register.platforms.other.title}. ${trans._register.platforms.other.subtitle}`"
            >
              <div class="tw:text-center">
                <div class="tw:body-1 tw:font-medium tw:text-black">
                  {{ trans._register.platforms.other.title }}
                </div>
              </div>
            </div>
          </button>
        </div>

        <div class="tw:hidden tw:md:block">
          <EdroneButton
            class="tw:w-full tw:transition-all tw:duration-200"
            :disabled="!selectedPlatform || isLoading"
            :aria-label="`${trans._register.buttons.next} - ${
              selectedPlatform ? platformData[selectedPlatform]?.name : ''
            }`"
            @click="handleNext"
          >
            <span v-if="isLoading" class="tw:flex tw:items-center tw:gap-2 tw:justify-center">
              <svg class="tw:animate-spin tw:h-4 tw:w-4" fill="none" viewBox="0 0 24 24">
                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="tw:opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ trans._register.buttons.loading }}
            </span>
            <span v-else class="tw:flex tw:items-center tw:gap-2 tw:justify-center">
              <span>{{ trans._register.platforms.startFreeTrial }}</span>
              <i class="fas fa-arrow-right tw:text-sm"></i>
            </span>
          </EdroneButton>
          <p class="tw:text-center tw:text-xs tw:text-default-600 tw:mt-2">
            *{{ trans._register.platforms.noCardRequired }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:bg-white tw:border-t tw:border-default-200 tw:p-4 tw:z-20 tw:shadow-lg tw:md:hidden"
    >
      <div class="tw:max-w-xs tw:sm:max-w-xl tw:mx-auto tw:flex tw:flex-col tw:gap-4">
        <div class="tw:flex tw:flex-col tw:gap-2">
          <EdroneButton
            class="tw:w-full tw:transition-all tw:duration-200"
            :disabled="!selectedPlatform || isLoading"
            :aria-label="`${trans._register.buttons.next} - ${
              selectedPlatform ? platformData[selectedPlatform]?.name : ''
            }`"
            @click="handleNext"
          >
            <span v-if="isLoading" class="tw:flex tw:items-center tw:gap-2 tw:justify-center">
              <svg class="tw:animate-spin tw:h-4 tw:w-4" fill="none" viewBox="0 0 24 24">
                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                  class="tw:opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ trans._register.buttons.loading }}
            </span>
            <span v-else class="tw:flex tw:items-center tw:gap-2 tw:justify-center">
              <span>{{ trans._register.buttons.next }}</span>
              <i class="fas fa-arrow-right tw:text-sm"></i>
            </span>
          </EdroneButton>
          <p class="tw:caption tw:text-center tw:text-default-600">*{{ trans._register.platforms.noCardRequired }}</p>
        </div>
        <div class="tw:flex tw:gap-1 tw:items-center tw:justify-center">
          <p class="tw:caption tw:text-default-700">{{ trans._register.platforms.haveAccount }}</p>
          <a href="/login" class="tw:caption--link tw:text-default-700">{{ trans._register.platforms.login }}</a>
        </div>
      </div>
    </div>
  </div>
  <RegisterPlatformsModal
    :is-open="showModal"
    :language="language"
    :selected-platform="selectedPlatform"
    @close="handleModalClose"
    @select="handleModalPlatformSelect"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick } from "vue";
import { storeToRefs } from "pinia";
import EdroneBadge from "vue-app/edroneComponents/EdroneBadge/EdroneBadge.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";
import {
  languagePlatforms,
  platformData,
} from "vue-app/components/Register/RegisterPlatforms/RegisterPlatforms.constants";
import { useRegisterStore } from "vue-app/pages/PageRegister/PageRegister.store";
import { useRouter } from "vue-router";
import RegisterPlatformsModal from "vue-app/components/Register/RegisterPlatforms/RegisterPlatformsModal.vue";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

const router = useRouter();
const store = useRegisterStore();
const { language, selectedPlatform } = storeToRefs(store);
const showModal = ref(false);
const isLoading = ref(false);

const trans = window._trans;

const platforms = computed(() => {
  const lang = language.value as keyof typeof languagePlatforms;
  const allowedPlatforms = languagePlatforms[lang] || [];

  return Object.fromEntries(allowedPlatforms.map((platform) => [platform, platformData[platform]]));
});

// Memoized computed for better performance
const isSelectedPlatformFromModal = computed(() => {
  if (!selectedPlatform.value) return false;
  const lang = language.value as keyof typeof languagePlatforms;
  const allowedPlatforms = languagePlatforms[lang] || [];
  return !allowedPlatforms.includes(selectedPlatform.value);
});

// Memoized computed for selected platform data
const selectedPlatformData = computed(() => {
  if (isSelectedPlatformFromModal.value && selectedPlatform.value) {
    return platformData[selectedPlatform.value];
  }
  return null;
});

// Track platform selection with improved UX
const handlePlatformSelect = async (key: string | number) => {
  selectedPlatform.value = key;

  // Add haptic feedback on supported devices, respecting reduced motion preference
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if ("vibrate" in navigator && !prefersReducedMotion) {
    navigator.vibrate(50);
  }

  amplitudeSendEvent("register_platform_selected", {
    platform: String(key),
    language: language.value,
    step: "platform_selection",
    source: "grid",
  });

  // Focus management for better a11y
  await nextTick();
  const selectedButton = document.querySelector(`[aria-checked="true"]`) as HTMLElement;
  selectedButton?.focus();
};

// Track modal opening for "other" platforms
const handleModalOpen = () => {
  showModal.value = true;
  amplitudeSendEvent("register_platform_modal_opened", {
    language: language.value,
    step: "platform_selection",
  });
};

// Track platform selection from modal
const handleModalPlatformSelect = (platform: string) => {
  selectedPlatform.value = platform;
  showModal.value = false;
  amplitudeSendEvent("register_platform_selected", {
    platform: platform,
    language: language.value,
    step: "platform_selection",
    source: "modal",
  });
};

// Track modal closing
const handleModalClose = () => {
  showModal.value = false;
  amplitudeSendEvent("register_platform_modal_closed", {
    language: language.value,
    step: "platform_selection",
    platform_selected: selectedPlatform.value ? String(selectedPlatform.value) : false,
  });
};

const handleNext = async () => {
  if (selectedPlatform.value && !isLoading.value) {
    isLoading.value = true;

    try {
      amplitudeSendEvent("register_platform_continue", {
        platform: String(selectedPlatform.value),
        language: language.value,
        step: "platform_selection",
        next_step: "form",
      });

      await router.push({
        name: "registerForm",
        query: { ...router.currentRoute.value.query },
      });
    } catch (error) {
      console.error("Navigation error:", error);
    } finally {
      isLoading.value = false;
    }
  }
};

onMounted(() => {
  window.scrollTo(0, 0);
});
</script>
